import { Category } from "@/dto/category";
import { ImageType } from "@/dto/image";
import { Item } from "@/dto/item";
import { Menu } from "@/dto/menu";
import { LaunchDarklyFeature, Merchant } from "@/dto/merchant";
import { Modifier } from "@/dto/modifier";
import { ModifierGroup } from "@/dto/modifierGroup";
import { DayPart } from "@/dto/dayPartMenu";
import { Hours } from "@/dto/hours";
import { Service } from "@/dto/service";
import { TableData } from "@/dto/tableData";

export const applyDataTestId = (testId: string) => {
  return testId
    .replace(/[\[\]'"]/g, "")
    .split("=")
    .pop();
};

export const extractDataTestId = (inputString: string): string | null => {
  const regex = /\[data-testid='([^']+)'\]/;
  const match = inputString.match(regex);

  if (match && match[1]) {
    return match[1];
  } else {
    return null;
  }
};

// Recursively optional variant so mock overrides can supply partial nested
// objects (e.g. a partial `address` or `merchantMeta`) without TypeScript
// demanding every field of the nested DTO.
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? DeepPartial<U>[]
    : T[P] extends object
      ? DeepPartial<T[P]>
      : T[P];
};

// Helper to create a mock merchant without needing all required fields
export const createMockMerchant = (
  features: LaunchDarklyFeature[] | undefined,
  overrides: DeepPartial<Merchant> = {}
): Merchant => {
  const baseMerchant = {
    id: "123",
    name: "Test Merchant",
    timezone: "America/New_York",
    locale: "en-US",
    launchDarklyFeatures: features || [],
    merchantMeta: {
      colorPalette: { color: "#000000" },
    },
    address: {
      address1: "123 Main St",
      city: "Atlanta",
      state: "GA",
      zip: "30303",
    },
    services: [],
    isService: false,
    isRestaurant: false,
  };

  return {
    ...baseMerchant,
    ...overrides,
  } as unknown as Merchant;
};

export const createMockModifier = (
  overrides: Partial<Modifier> = {}
): Modifier => ({
  id: "mod-1",
  name: "Mock Modifier",
  price: 0,
  available: true,
  ...overrides,
});

export const createMockModifierGroup = (
  overrides: Partial<ModifierGroup> = {}
): ModifierGroup => ({
  id: "group-1",
  name: "Mock Group",
  minRequired: 0,
  maxAllowed: 1,
  modifiers: [],
  ...overrides,
});

export const createMockItem = (overrides: Partial<Item> = {}): Item => ({
  id: "item-1",
  name: "Mock Item",
  price: 0,
  modifierGroupIds: [],
  categoryIds: [],
  taxIds: [],
  images: [],
  available: true,
  options: [],
  serviceItem: [],
  modifiedTime: Date.now(),
  isAgeRestricted: false,
  ...overrides,
});

export const createMockMenu = (overrides: Partial<Menu> = {}): Menu => ({
  categories: {},
  modifierGroups: {},
  modifiers: [],
  items: [],
  ...overrides,
});

export const createMockImage = (
  overrides: Partial<ImageType> = {}
): ImageType => ({
  name: "test-image",
  source: "https://example.com/image.jpg",
  ...overrides,
});

export const createMockCategory = (
  overrides: Partial<Category> = {}
): Category => ({
  id: "cat-123",
  name: "Mock Category",
  sortOrder: 1,
  items: [],
  ...overrides,
});

export const createMockCategories = (count: number): Category[] =>
  Array.from({ length: count }, (_, i) =>
    createMockCategory({ id: `cat-${i}`, name: `Category ${i}`, sortOrder: i })
  );

export const createMockGiftCardCategory = (): Category => ({
  id: "GIFTCARD",
  name: "Gift Card",
  sortOrder: 999,
  items: ["GIFTCARD_ITEM"],
});

export const createMockDayPart = (
  overrides: Partial<DayPart> = {}
): DayPart => ({
  id: "default-dp-id",
  name: "Default Day Part",
  days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  time: {
    startTime: 900,
    endTime: 1700,
  },
  formattedName: "Default Menu",
  availabilityLabel: "Open now",
  activeNow: false,
  upcoming: false,
  menuId: "default-menu-id",
  menuName: "Default Menu Name",
  ...overrides,
});

export const createMockBusinessHours = (
  todaySlots: { start: string; end: string }[] = []
) => {
  const todayKey = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();

  const emptySchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  return {
    hours: {
      ...emptySchedule,
      [todayKey]: todaySlots,
    },
  };
};

export const createMockService = (
  overrides: Partial<Service> = {}
): Service => {
  return {
    id: "service-123",
    type: "PICKUP",
    enabled: true,
    orderType: {
      id: "2",
    },
    hours: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    } as unknown as Hours,
    ...overrides,
  };
};

export const createMockTableData = (
  overrides: Partial<TableData> = {}
): TableData => {
  return {
    id: "123",
    name: "Table 1",
    isValid: true,
    section: {
      id: "sec-1",
      name: "Main Dining",
    },
    order: {
      id: "order-1",
      additionalCharges: [],
      cart: {
        id: "cart-1",
        discounts: [],
        items: [],
      },
      metadata: {
        scheduled: false,
        scheduledPickupTime: 0,
      },
      note: "",
      paymentStatus: "OPEN",
      timePlaced: 1670000000000,
      total: 0,
      type: "DINE_IN",
    },
    ...overrides,
  };
};
